import { useCallback, useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse, stringify } from "qs";

import getStoreLocatorResults, {
  StoreLocatorResultsResponse,
} from "@/api/requests/consumerApi/getStoreLocatorResults";
import { getCopyBlock } from "@/api/requests/directus/getCopyBlock";
import { getDirectusFAQs } from "@/api/requests/directus/getDirectusFAQs";
import getFinderCitiesNearMe from "@/api/requests/getFinderCitiesNearMe";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import publicConfig from "@/config/public";
import { StoreLocatorProvider } from "@/context/StoreLocatorContext";
import { City } from "@/custom-types/CitiesNearMe";
import { CountryCode } from "@/custom-types/CountryCode";
import { CopyBlock, FAQ } from "@/custom-types/Directus";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import { Location } from "@/custom-types/Location";
import { Query } from "@/custom-types/Query";
import { RetailType } from "@/custom-types/Store";
import { MapData } from "@/custom-types/StoreLocator";
import { FulfillmentPreference } from "@/custom-types/User";
import useCustomLocationPickerUpdateBehavior from "@/hooks/useCustomLocationPickerUpdateBehavior";
import useUserFulfillmentPreference from "@/hooks/useUserFulfillmentPreference";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getCountryCodeHeader } from "@/utils/requestContext";
import { getOrganizationData } from "@/utils/storeLocator/getContextData";
import { getLocation } from "@/utils/storeLocator/getLocation";
import getRetailTypeFromUrl from "@/utils/storeLocator/getRetailTypeFromUrl";
import getStoreLocatorRoute from "@/utils/storeLocator/getStoreLocatorRoute";
import replaceLegacyFinderFilters from "@/utils/storeLocator/replaceLegacyFinderFilters";

import { getCbdStores } from "@/components/dispensaries/CBDStoresCarousel/CBDStoresCarousel";
import ContentSection from "@/components/dispensaries/ContentSection";
import DispensariesHead from "@/components/dispensaries/DispensariesHead/DispensariesHead";
import MapSection from "@/components/dispensaries/MapSection";

import { getMapSetup } from "../../utils/storeLocator/getMapSetup";

export interface DispensariesPageProps {
  cbdStores: DispensaryV3[];
  faqs: FAQ | null;
  filter: Query;
  copyBlockData: CopyBlock | null;
  hideFooter: boolean;
  initMapData: MapData;
  isLocationPage: boolean;
  retailType: RetailType;
  cities: City[];
  storeLocatorResults: StoreLocatorResultsResponse;
  pageLocation: Location;
  showDeliveryValidation: boolean;
  countryCode: CountryCode;
  page: number;
}

export const DispensariesPage: React.FC<DispensariesPageProps> = ({
  cbdStores,
  copyBlockData,
  faqs,
  initMapData,
  isLocationPage,
  retailType,
  storeLocatorResults,
  cities,
  pageLocation,
  showDeliveryValidation,
  filter,
  countryCode,
  page,
}) => {
  const pathname = usePathname();
  const { replace } = useRouter();
  const searchParams = useSearchParams();
  const { setUserFulfillmentPreference } = useUserFulfillmentPreference();

  useEffect(() => {
    const pickupEnabled = filter?.["pickup-enabled"] === "true";
    const deliveryEnabled = filter?.["delivery-enabled"] === "true";

    if (!pickupEnabled && !deliveryEnabled) {
      return;
    }

    // Only set preference if one or the other is selected
    if (pickupEnabled !== deliveryEnabled) {
      const fulfillmentPreference = pickupEnabled
        ? FulfillmentPreference.PICKUP
        : FulfillmentPreference.DELIVERY;

      setUserFulfillmentPreference(fulfillmentPreference);
    }
  }, [filter]);

  const handleLocationUpdate = useCallback(() => {
    const [_, routeRoot, _routeLocaleSlug, _routeSubLocaleSlug] =
      pathname?.split("/") || [];

    const targetSearchParams = new URLSearchParams(searchParams?.toString());
    targetSearchParams.delete("top_lat");
    targetSearchParams.delete("bottom_lat");
    targetSearchParams.delete("left_lon");
    targetSearchParams.delete("right_lon");
    targetSearchParams.delete("page");
    targetSearchParams.delete("country");
    const targetQueryString =
      targetSearchParams.toString().length > 0
        ? `?${targetSearchParams.toString()}`
        : "";

    let destinationPath = `/${routeRoot}`;
    if (targetQueryString) {
      destinationPath += `/${targetQueryString}`;
    }
    replace(destinationPath);
  }, [pathname, replace, searchParams]);

  useCustomLocationPickerUpdateBehavior(handleLocationUpdate);

  return (
    <>
      <DispensariesHead
        seo={storeLocatorResults.metadata.seo}
        faqs={faqs}
        stores={[
          ...storeLocatorResults.data.sponsoredStores,
          ...storeLocatorResults.data.organicStores,
        ]}
      />
      <>
        <div className="stores-page relative md:grid md:grid-cols-[var(--stores-desktop-listing-width),_1fr] h-full w-full [&_p]:mb-0">
          <StoreLocatorProvider>
            <ContentSection
              retailType={retailType}
              copyBlockData={copyBlockData}
              faqs={faqs}
              cities={cities}
              storeLocatorResults={storeLocatorResults}
              page={page}
              isLocationPage={isLocationPage}
              pageLocation={pageLocation}
              showDeliveryValidation={showDeliveryValidation}
              cbdStores={cbdStores}
              countryCode={countryCode}
              deliveryFilterSelected={filter?.["delivery-enabled"] === "true"}
            />
            <MapSection
              retailType={retailType}
              initMapData={initMapData}
              spotlightStores={storeLocatorResults.data.spotlightStores}
              stores={[
                ...storeLocatorResults.data.sponsoredStores,
                ...storeLocatorResults.data.organicStores,
              ]}
            />
          </StoreLocatorProvider>
        </div>

        <style jsx global>{`
          .stores-page {
            --stores-desktop-listing-width: 380px;
            --stores-desktop-min-breakpoint: 1023px;
            --stores-collapsed-header-height: 48px;
            --stores-mobile-bottom-touchpoint: 104px;
            --stores-mobile-top-touchpoint: var(
              --stores-collapsed-header-height
            );
          }

          html,
          body,
          #modal-frame,
          #__next {
            height: 100%;
            max-height: 100%;
          }

          main {
            height: 100%;
            max-height: calc(
              100% - var(--header-height) - var(--app-banner-height)
            );
          }
        `}</style>
      </>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<DispensariesPageProps>(async (context) => {
    const { query, resolvedUrl } = context;
    const [url] = resolvedUrl.split("#");
    const [path, qparams] = url.split("?");
    const {
      filter: rawFilter,
      page = "1",
      sort,
      organization_id,
      country,
    } = parse(qparams);
    const filter = { ...(rawFilter as Query) };

    const newQueryString = replaceLegacyFinderFilters(query);
    if (newQueryString) {
      return {
        redirect: {
          destination: `${path}${newQueryString ? `?${newQueryString}` : ""}`,
          statusCode: 301,
        },
      };
    }

    const countryCode = getCountryCodeHeader(context.req.headers);
    const retailType = getRetailTypeFromUrl(resolvedUrl);

    const pageNumber = Number(page);

    // TODO: Refactor getLocation - https://leafly.atlassian.net/browse/NL-1997
    const { finderLocation, isLocationPage } = await getLocation(
      context,
      country as string | undefined,
    );
    const pageLocation = finderLocation as Location;
    const userLocation = await getUserLocationData(countryCode, context);

    const initMapData = getMapSetup(query, pageLocation);

    const organization =
      typeof organization_id === "string"
        ? { id: parseInt(organization_id) }
        : await getOrganizationData(query);

    const showDeliveryValidation = query["filter[delivery-enabled]"] === "true";

    const [storeLocatorResults, cities] = await Promise.all([
      getStoreLocatorResults({
        contextSlug: query?.location?.[1],
        filter,
        isLocationPage,
        location: pageLocation,
        mapData: initMapData,
        organizationId: organization?.id,
        page: pageNumber,
        retailType,
        sort: sort as string | undefined,
        userLat: userLocation?.coordinates?.latitude,
        userLon: userLocation?.coordinates?.longitude,
      }),
      getFinderCitiesNearMe({
        countryCode,
        location: pageLocation,
        retailType,
      }),
    ]);

    const slug =
      storeLocatorResults.metadata.seo.canonicalUrl ||
      getStoreLocatorRoute(retailType, countryCode);

    const [{ data: faqData }, copyBlockData, cbdStores] = await Promise.all([
      getDirectusFAQs("faq", slug, countryCode),
      getCopyBlock("copy_block", slug, countryCode),
      getCbdStores({
        countryCode,
        filter,
        pageLocation,
        path,
        storeLocatorResults,
      }),
    ]);

    const lastPage = storeLocatorResults.metadata.pagination.totalCount;

    if (pageNumber > 1 && pageNumber > lastPage) {
      const newQueryString = parse(qparams);
      newQueryString.page = (lastPage || 1).toString();

      return {
        redirect: {
          destination: `${path}?${stringify(newQueryString, {
            arrayFormat: "brackets",
            indices: false,
          })}`,
          statusCode: 302,
        },
      };
    }

    const { leaflyDomain, leaflyCaDomain } = publicConfig;

    const urlPath = new URL(
      resolvedUrl,
      countryCode === "CA" ? leaflyCaDomain : leaflyDomain,
    );

    // When changing location using the LocationPicker, `?country=United%20States` is added to the url - this removes it so we can accurately compare the paths
    urlPath.searchParams.delete("country");

    const normalizedResolvedUrl =
      urlPath.pathname +
      (urlPath.search ? `?${urlPath.searchParams.toString()}` : "");

    // Do not index when we get a malformed location (i.e. `cerritos_ ca`)
    if (slug !== normalizedResolvedUrl) {
      storeLocatorResults.metadata.seo.indexEnabled = false;
    }

    return {
      props: {
        cbdStores,
        cities,
        copyBlockData,
        countryCode,
        disableHeaderExposeScroll: true,
        faqs: faqData,
        filter,
        hideFooter: true,
        initMapData,
        isLocationPage,
        page: pageNumber,
        pageLocation,
        retailType,
        showDeliveryValidation,
        storeLocatorResults,
      },
    };
  });

export default DispensariesPage;
